import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { formatCurrency } from '../../mock';
import { CsvButton, DataTable, GroupTabs, PeriodBar, Ribbon, Select, PRESETS } from './shared';

const GROUPS = [['source', 'Fonte'], ['salesperson', 'Vendedor'], ['status', 'Status'], ['stage', 'Etapa CRM'], ['month', 'Mês']];
const STATUSES = ['NEW', 'BDC_ASSIGNED', 'CONTACTING', 'FOLLOW_UP', 'APPOINTMENT_SCHEDULED', 'AWAITING_SALES_ASSIGNMENT', 'ASSIGNED_TO_SALESPERSON', 'LOST', 'CRM'];
const mins = (v) => (v == null ? '—' : v < 60 ? `${v}min` : v < 1440 ? `${Math.floor(v / 60)}h${v % 60 ? ` ${v % 60}min` : ''}` : `${Math.floor(v / 1440)}d`);

export const LeadsReport = ({ opts, mine }) => {
  const month = PRESETS().find(([k]) => k === 'month')[2];
  const [range, setRange] = useState(month);
  const [f, setF] = useState({ salesperson: '', source: '', status: '', only_sold: false });
  const [data, setData] = useState(null);
  const [group, setGroup] = useState(mine ? 'source' : 'salesperson');
  const [loading, setLoading] = useState(true);

  const qs = useCallback((r = range, ff = f) => {
    const p = new URLSearchParams({ date_from: r.from || '', date_to: r.to || '', salesperson: ff.salesperson, source: ff.source, status: ff.status });
    if (ff.only_sold) p.set('only_sold', 'true');
    if (mine) p.set('mine', 'true');
    return p.toString();
  }, [range, f, mine]);

  const load = useCallback(async (r, ff) => {
    setLoading(true);
    try { const { data } = await api.get(`/reports/leads?${qs(r || range, ff || f)}`); setData(data); }
    catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [qs, range, f]);

  useEffect(() => { load(month); }, []); // eslint-disable-line

  const k = data?.kpis || {};
  const gcols = [
    { key: 'key', label: GROUPS.find(([g]) => g === group)[1].toUpperCase(), render: (r) => <span className="font-semibold">{r.key || '—'}</span> },
    { key: 'leads', label: 'LEADS', align: 'right' },
    { key: 'contacted', label: 'CONTATADOS', align: 'right' },
    { key: 'contact_rate', label: '% CONTATO', align: 'right', render: (r) => `${r.contact_rate}%` },
    { key: 'avg_first_contact_min', label: '1º CONTATO', align: 'right', render: (r) => mins(r.avg_first_contact_min || null) },
    { key: 'appointments', label: 'AGEND.', align: 'right' },
    { key: 'appt_rate', label: '% AGEND.', align: 'right', render: (r) => `${r.appt_rate}%` },
    { key: 'sold', label: 'VENDEU', align: 'right', render: (r) => <span className={r.sold ? 't-ok font-bold' : ''}>{r.sold}</span> },
    { key: 'conversion', label: 'CONVERSÃO', align: 'right', render: (r) => <span className={r.conversion >= 10 ? 't-ok' : r.conversion > 0 ? '' : 't-warn'}>{r.conversion}%</span> },
    { key: 'lost', label: 'PERDIDOS', align: 'right' },
    { key: 'revenue', label: 'RECEITA', align: 'right', render: (r) => formatCurrency(r.revenue || 0) },
  ];

  const cols = [
    { key: 'created_at', label: 'RECEBIDO', render: (r) => <span className="num text-muted-foreground">{r.created_at || '—'}</span> },
    { key: 'name', label: 'LEAD', render: (r) => (<div><p className="font-semibold">{r.name}</p><p className="text-[10px] text-muted-foreground/70">{r.phone || '—'} · {r.kind}</p></div>) },
    { key: 'source', label: 'FONTE', render: (r) => <span className="ent-tag">{r.source}</span> },
    { key: 'campaign', label: 'CAMPANHA', hide: 'hidden xl:table-cell' },
    { key: 'vehicle_interest', label: 'INTERESSE', hide: 'hidden lg:table-cell' },
    { key: 'salesperson', label: 'VENDEDOR', render: (r) => r.salesperson || <span className="t-warn">não atribuído</span> },
    { key: 'first_contact_minutes', label: '1º CONTATO', align: 'right', render: (r) => mins(r.first_contact_minutes) },
    { key: 'stage', label: 'ETAPA/STATUS', render: (r) => <span className="text-[11px]">{r.stage || r.status}</span> },
    { key: 'age_days', label: 'ABERTO', align: 'right', render: (r) => `${r.age_days}d` },
    { key: 'sold', label: 'RESULTADO', render: (r) => (r.sold ? <span className="t-ok font-bold">Vendeu · {r.deal_id}</span> : r.lost ? <span className="t-bad">Perdido{r.lost_reason ? ` · ${r.lost_reason}` : ''}</span> : <span className="text-muted-foreground">Em aberto</span>) },
  ];

  return (
    <div className="space-y-3" data-testid="leads-report">
      <PeriodBar range={range} setRange={setRange} onApply={(r) => load(r)} testid="leads-filters">
        {!mine && opts?.is_manager && <Select label="Vendedor" testid="lead-filter-salesperson" value={f.salesperson} onChange={(v) => setF({ ...f, salesperson: v })} options={opts?.salespeople || []} />}
        <Select label="Fonte" testid="lead-filter-source" value={f.source} onChange={(v) => setF({ ...f, source: v })} options={opts?.sources || []} />
        <Select label="Status" testid="lead-filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={STATUSES} />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground h-8 px-1 cursor-pointer">
          <input data-testid="lead-filter-sold" type="checkbox" checked={f.only_sold} onChange={(e) => setF({ ...f, only_sold: e.target.checked })} /> só convertidos
        </label>
        <CsvButton testid="leads-csv" url={`/reports/leads/csv?${qs()}`} filename="relatorio_leads.csv" />
      </PeriodBar>

      {loading ? <p className="ent-empty">Calculando relatório…</p> : !data ? <p className="ent-empty">Não foi possível carregar o relatório.</p> : (
        <>
          <Ribbon testid="leads-kpis" items={[
            [mine ? 'Leads recebidos por mim' : 'Leads recebidos', k.leads],
            ['Contatados', k.contacted],
            ['% contato', `${k.contact_rate}%`, k.contact_rate >= 80 ? 't-ok' : 't-warn'],
            ['Sem contato', k.no_contact, k.no_contact ? 't-bad' : ''],
            ['1º contato médio', mins(k.avg_first_contact_min || null)],
            ['Agendamentos', k.appointments],
            ['% agendamento', `${k.appointment_rate}%`],
            ['Convertidos em venda', k.sold, 't-ok'],
            ['Conversão', `${k.conversion}%`, k.conversion >= 10 ? 't-ok' : 't-warn'],
            ['Receita gerada', formatCurrency(k.revenue || 0)],
            ['Perdidos', k.lost],
            ['Idade média em aberto', `${k.avg_age_days}d`],
          ]} />

          <div className="ent-panel">
            <div className="ent-panel-head"><span className="ent-label">Performance por</span></div>
            <GroupTabs testid="leads-group" tabs={GROUPS} value={group} onChange={setGroup} />
            <DataTable testid="leads-group-table" cols={gcols} rows={data.groups?.[group] || []} rowKey="key" />
          </div>

          {!!data.lost_reasons?.length && (
            <div className="ent-panel">
              <div className="ent-panel-head"><span className="ent-label">Motivos de perda</span></div>
              <DataTable testid="leads-lost" rowKey="key" rows={data.lost_reasons} cols={[
                { key: 'key', label: 'MOTIVO', render: (r) => <span className="font-semibold">{r.key}</span> },
                { key: 'count', label: 'LEADS', align: 'right' },
              ]} />
            </div>
          )}

          <div className="ent-panel">
            <div className="ent-panel-head">
              <span className="ent-label">Leads detalhados</span>
              <span className="ent-pill">{data.row_count}</span>
            </div>
            <DataTable testid="leads-rows" cols={cols} rows={data.rows || []} rowKey="id" empty="Nenhum lead no período com esses filtros" />
          </div>
        </>
      )}
    </div>
  );
};
